import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import isEmail from 'validator/lib/isEmail';
import { requestPasswordReset } from './api';
import { SubmitButton } from './SubmitButton';
import { MAX_EMAIL } from './fieldLimits';
import { autofillSx } from './fieldStyles';
import { useFormSubmit } from './useFormSubmit';

interface Props {
  onCodeSent: (email: string) => void;
  onBack: () => void;
}

/**
 * Step 1 of the forgot-password flow: collect the email and request a reset code.
 * The API always responds with { status: 'OK' } — whether or not the email is
 * registered — so this form always advances to the "enter code" step and shows the
 * same generic message. This is deliberate: it prevents an attacker from using this
 * form to discover which emails have accounts.
 */
export const ForgotPasswordForm = ({ onCodeSent, onBack }: Props) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const { loading, error, setError, runSubmit } = useFormSubmit();

  /** Same check as LocalLoginForm — see the note there; this form is `noValidate` too. */
  const isEmailInvalid = email.length > 0 && !isEmail(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEmailInvalid) return;
    await runSubmit(async () => {
      await requestPasswordReset(email);
      onCodeSent(email);
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2.5}>
        <Typography variant="body2" color="text.secondary">
          {t('login.forgotPassword.description')}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          id="forgot-password-email"
          label={t('formField.Email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          autoFocus
          fullWidth
          size="small"
          error={isEmailInvalid}
          helperText={isEmailInvalid ? t('login.local.invalidEmail') : undefined}
          inputProps={{ maxLength: MAX_EMAIL }}
          sx={autofillSx}
        />

        <SubmitButton loading={loading} disabled={!email || isEmailInvalid}>
          {t('login.forgotPassword.submit')}
        </SubmitButton>

        <Button variant="text" size="small" onClick={onBack} sx={{ alignSelf: 'center' }}>
          {t('action.backToLogin')}
        </Button>
      </Stack>
    </Box>
  );
};
